import type {
  Middleware,
  ResponseContext,
} from "@freightsimple/generated-dashboard-openapi-client/runtime";

// The backend answers 429 + Retry-After when Auth0's management API rate limit
// trips (user management calls: list-for-company, look-up-email, etc). Without
// a retry, the generated client throws ResponseError — which almost no flow
// catches, so the page crashes on a transient throttle. Waiting out the window
// is cheap and invisible; give up after a couple of attempts so a genuinely
// stuck backend still surfaces as an error.
const MAX_RATE_LIMIT_RETRIES = 2;
const DEFAULT_RETRY_AFTER_MS = 5_000;
// A browser tab shouldn't hang for a long server-suggested window; cap it.
const MAX_RETRY_AFTER_MS = 15_000;
// Jitter so parallel tabs / Cypress nodes don't stampede back in the same second.
const MAX_JITTER_MS = 1_000;

// A Retry-After beyond the cap is a block, not a throttle: the Dashboard API's own
// rate limiter answers with minutes. Two 15 s waits cannot clear it, so fail now and
// let the caller surface the error instead of hanging.
function isWorthRetrying(response: Response): boolean {
  const retryAfterSeconds = Number(response.headers.get("retry-after"));
  return !(
    Number.isFinite(retryAfterSeconds) &&
    retryAfterSeconds * 1000 > MAX_RETRY_AFTER_MS
  );
}

export function rateLimitWaitMs(response: Response): number {
  const retryAfterSeconds = Number(response.headers.get("retry-after"));
  const waitMs =
    Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? Math.min(retryAfterSeconds * 1000, MAX_RETRY_AFTER_MS)
      : DEFAULT_RETRY_AFTER_MS;
  return waitMs + Math.floor(Math.random() * MAX_JITTER_MS);
}

interface RateLimitRetryDependencies {
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export interface RateLimitRetryMiddleware extends Middleware {
  post: (context: ResponseContext) => Promise<Response>;
}

export function rateLimitRetryMiddleware(
  dependencies?: RateLimitRetryDependencies,
): RateLimitRetryMiddleware {
  const sleep = dependencies?.sleep ?? defaultSleep;
  return {
    post: async (context: ResponseContext): Promise<Response> => {
      let response = context.response;
      for (
        let attempt = 0;
        response.status === 429 &&
        isWorthRetrying(response) &&
        attempt < MAX_RATE_LIMIT_RETRIES;
        attempt++
      ) {
        await sleep(rateLimitWaitMs(response));
        // Deliberately NOT context.fetch — that re-enters the middleware chain
        // and would compound retries recursively.
        const fetchImpl = dependencies?.fetchImpl ?? fetch;
        response = await fetchImpl(context.url, context.init);
      }
      return response;
    },
  };
}
